import { GameSettingBaseProps } from "components/common/GameSettingModal";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BlackjackGameMode, BlackjackGameSettings } from "./DealerJackProtocol";
import SocketProtocol from "network/SocketProtocol";
import SocketService from "network/SocketService";

function DealerJackGameSettingBody({
  setGameSetting,
  roomParams: gameParams,
  aiCount,
}: GameSettingBaseProps) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<BlackjackGameSettings>({
    gameMode: BlackjackGameMode.DEALER, // 딜러 모드로 고정
    totalRounds: 5,
    startingChips: 100,
    bettingTime: 15,
    turnTime: 15,
    minBet: 10,
  });

  const handleAI = (add: boolean) => {
    SocketService.getInstance().emit(
      SocketProtocol.ADD_AI_PLAYER,
      gameParams.product.id,
      gameParams.room.roomName,
      add
    );
  };

  useEffect(() => {
    setGameSetting(settings);
  }, [settings, setGameSetting]);

  return (
    <div className="mt-4 flex flex-col items-center justify-center gap-6">
      {/* AI 봇 수 */}
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-4">
          <div className="text-gray-200 min-w-[120px] text-sm">
            {t("aiPlayer")}
          </div>
          <div className="flex items-center gap-1 px-2 bg-gray-700 rounded-lg">
            <button
              className="!w-[32px] text-white text-lg hover:bg-gray-600 rounded-l-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handleAI(false)}
              disabled={aiCount <= 0}
            >
              –
            </button>
            <span className="text-white min-w-[50px] text-center text-sm">
              {aiCount}
            </span>
            <button
              className="!w-[32px] text-white text-lg hover:bg-gray-600 rounded-r-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handleAI(true)}
              disabled={aiCount >= 5}
            >
              +
            </button>
          </div>
        </div>
      </div>

      {/* 라운드 설정 */}
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-4">
          <div className="text-gray-200 min-w-[120px] text-sm">
            {t("round")}
          </div>
          <div className="flex items-center gap-1 px-2 bg-gray-700 rounded-lg">
            <button
              className="!w-[32px] text-white text-lg hover:bg-gray-600 rounded-l-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() =>
                setSettings({
                  ...settings,
                  totalRounds: Math.max(3, settings.totalRounds - 1),
                })
              }
              disabled={settings.totalRounds <= 3}
            >
              –
            </button>
            <span className="text-white min-w-[50px] text-center text-sm">
              {settings.totalRounds}
            </span>
            <button
              className="!w-[32px] text-white text-lg hover:bg-gray-600 rounded-r-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() =>
                setSettings({
                  ...settings,
                  totalRounds: Math.min(10, settings.totalRounds + 1),
                })
              }
              disabled={settings.totalRounds >= 10}
            >
              +
            </button>
          </div>
        </div>
      </div>

      {/* 시작 칩 */}
      <div className="w-full max-w-md">
        <div className="text-center text-gray-300 text-sm mb-2">
          {t("Dealerjack.startingChips")}
        </div>
        <div className="flex flex-col gap-2">
          <input
            type="range"
            min={50}
            max={200}
            step={10}
            value={settings.startingChips}
            onChange={(e) =>
              setSettings({
                ...settings,
                startingChips: parseInt(e.target.value),
              })
            }
            className="w-full accent-yellow-500"
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>50</span>
            <span className="text-yellow-400 font-bold text-sm">
              {settings.startingChips}
            </span>
            <span>200</span>
          </div>
        </div>
      </div>

      {/* 최소 배팅 칩수 */}
      <div className="w-full max-w-md">
        <div className="text-center text-gray-300 text-sm mb-2">
          {t("Dealerjack.minBet")}
        </div>
        <div className="flex items-center justify-center gap-3">
          {[5, 10, 20].map((value) => (
            <button
              key={value}
              onClick={() =>
                setSettings({
                  ...settings,
                  minBet: value,
                })
              }
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                settings.minBet === value
                  ? "bg-yellow-500 text-black"
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600"
              }`}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      {/* 시간 설정 */}
      <div className="w-full max-w-md">
        <div className="text-center text-gray-300 text-sm mb-2">
          {t("Dealerjack.timeSettings")}
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-4">
            <div className="text-gray-200 min-w-[120px] text-sm">
              {t("Dealerjack.bettingTime")}
            </div>
            <div className="flex items-center gap-1 px-2 bg-gray-700 rounded-lg">
              <button
                className="!w-[32px] text-white text-lg hover:bg-gray-600 rounded-l-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() =>
                  setSettings({
                    ...settings,
                    bettingTime: Math.max(10, settings.bettingTime - 5),
                  })
                }
                disabled={settings.bettingTime <= 10}
              >
                –
              </button>
              <span className="text-white min-w-[50px] text-center text-sm">
                {settings.bettingTime}s
              </span>
              <button
                className="!w-[32px] text-white text-lg hover:bg-gray-600 rounded-r-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() =>
                  setSettings({
                    ...settings,
                    bettingTime: Math.min(60, settings.bettingTime + 5),
                  })
                }
                disabled={settings.bettingTime >= 60}
              >
                +
              </button>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4">
            <div className="text-gray-200 min-w-[120px] text-sm">
              {t("Dealerjack.turnTime")}
            </div>
            <div className="flex items-center gap-1 px-2 bg-gray-700 rounded-lg">
              <button
                className="!w-[32px] text-white text-lg hover:bg-gray-600 rounded-l-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() =>
                  setSettings({
                    ...settings,
                    turnTime: Math.max(10, settings.turnTime - 5),
                  })
                }
                disabled={settings.turnTime <= 10}
              >
                –
              </button>
              <span className="text-white min-w-[50px] text-center text-sm">
                {settings.turnTime}s
              </span>
              <button
                className="!w-[32px] text-white text-lg hover:bg-gray-600 rounded-r-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() =>
                  setSettings({
                    ...settings,
                    turnTime: Math.min(60, settings.turnTime + 5),
                  })
                }
                disabled={settings.turnTime >= 60}
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DealerJackGameSettingBody;
